import { useMemo, useRef, useState } from 'react'
import { ExternalLink, FileText, Plus, Trash2, Upload } from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { Chip } from '../interviews/Chip'
import { useRaiseCashRepair, useUploadCashRepairQuote } from '../../hooks/backend/cashRepairs'
import { useGetMaintenanceInvoiceUrl } from '../../hooks/backend/fleet'
import { writeErrorMessage } from '../../utils/writeError'
import { fileToBase64 } from '../../utils/marketing'
import { BORNE_TONE, MAINTENANCE_CATEGORIES, borneLabel, categoryLabel, zarExact } from '../../utils/servicing'
import { quotedCash, type CashRepairOptions, type QuoteReading } from '../../utils/cashRepairs'

// kind_key 'quote' marks an item filled from the quote: it carries the quote's
// own words and no kind from the list, and is sent with no kind at all.
type Draft = { kind_key: string; description: string; category: string; amount: string; hint?: string }

const blank = (): Draft => ({ kind_key: '', description: '', category: '', amount: '' })

/**
 * Ask for cash to repair one car. One request carries every item the car needs
 * today, each in its own category, because "windscreen, brake pads and two
 * bulbs" is one trip to one fitter and one approval, but three different
 * questions about who pays.
 */
export function RaiseCashRepairDialog({ open, onOpenChange, options, onSaved }: {
  open: boolean
  onOpenChange: (open: boolean) => void
  options: CashRepairOptions
  onSaved: () => void
}) {
  const [vehicleId, setVehicleId] = useState('')
  const [driverId, setDriverId] = useState('')
  const [reason, setReason] = useState('')
  const [items, setItems] = useState<Draft[]>([blank()])
  const [quote, setQuote] = useState<QuoteReading | null>(null)
  const quoteFile = useRef<HTMLInputElement>(null)
  const raise = useRaiseCashRepair<{ ref: string }>()
  const uploadQuote = useUploadCashRepairQuote<QuoteReading>()
  const quoteUrl = useGetMaintenanceInvoiceUrl<{ url: string }>()

  const vehicle = options.vehicles.find((v) => v.vehicle_id === vehicleId) ?? null
  const kinds = options.kinds
  const kindOf = (key: string) => kinds.find((k) => k.key === key) ?? null
  const categoryFor = (d: Draft) => d.category || kindOf(d.kind_key)?.default_category || ''
  const total = useMemo(() => items.reduce((s, d) => s + (Number(d.amount) || 0), 0), [items])
  const driverBorne = items.some((d) => options.borne_by_category[categoryFor(d)] === 'driver')

  function reset() {
    setVehicleId(''); setDriverId(''); setReason(''); setItems([blank()]); setQuote(null)
  }

  // The reader fills the items; the person checks them. Blank rows are replaced,
  // anything already typed is kept and the quote's items are added after it.
  async function readQuote(f: File) {
    if (!vehicleId) { toast.error('Choose the car first'); return }
    if (f.size > 10 * 1024 * 1024) { toast.error('That file is larger than 10 MB. Save it again at a smaller size.'); return }
    try {
      const contentBase64 = await fileToBase64(f)
      const r = await uploadQuote.trigger({ vehicle_id: vehicleId, filename: f.name, contentType: f.type, contentBase64 }).result
      setQuote(r)
      if (r.read_status !== 'read' || r.items.length === 0) {
        toast.warning('Quote filed, but not read', { description: r.read_error ?? 'Enter the items by hand; the quote stays attached.' })
        return
      }
      const fromQuote: Draft[] = r.items.map((i) => {
        const cash = quotedCash(i)
        return {
          kind_key: 'quote',
          description: i.description,
          category: i.suggested_category ?? '',
          amount: cash === null ? '' : String(cash),
          hint: i.suggested_category
            ? `The reader put this in ${categoryLabel(i.suggested_category)}${i.suggested_reason ? `: ${i.suggested_reason}` : '.'}`
            : undefined,
        }
      })
      setItems((cur) => [...cur.filter((d) => d.kind_key || d.description || d.amount), ...fromQuote])
      toast.success(`${r.items.length} item${r.items.length === 1 ? '' : 's'} read from the quote`, {
        description: 'Check each amount and category before raising.',
      })
    } catch (err) {
      toast.error('Could not read the quote', { description: writeErrorMessage(err) })
    } finally {
      if (quoteFile.current) quoteFile.current.value = ''
    }
  }

  async function openQuote() {
    if (!quote) return
    try {
      const r = await quoteUrl.trigger({ invoice_id: quote.invoice_id }, { skipCache: true }).result
      window.open(r.url, '_blank', 'noopener')
    } catch (err) {
      toast.error('Could not open the quote', { description: writeErrorMessage(err) })
    }
  }
  function patch(i: number, p: Partial<Draft>) {
    setItems((cur) => cur.map((d, j) => (j === i ? { ...d, ...p } : d)))
  }

  async function save() {
    try {
      const r = await raise.trigger({
        vehicle_id: vehicleId,
        driver_id: driverId || null,
        reason,
        quote_invoice_id: quote?.invoice_id ?? null,
        items: items.map((d) => ({
          kind_key: d.kind_key === 'quote' ? '' : d.kind_key, description: d.description || null,
          category: d.category || null, amount_zar: Number(d.amount),
        })),
      }).result
      toast.success(`${r.ref} raised`, { description: `Waiting for ${options.access.approvers ?? 'an approver'} to approve it.` })
      reset()
      onOpenChange(false)
      onSaved()
    } catch (err) {
      toast.error('Could not raise the cash repair', { description: writeErrorMessage(err) })
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) reset(); onOpenChange(next) }}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Raise a cash repair</DialogTitle>
          <DialogDescription>
            One car, every item it needs. {options.access.approvers ?? 'An approver'} approves it, and
            {' '}{options.access.payers ?? 'the payer'} pays it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="cr-vehicle">Car</Label>
              <NativeSelect id="cr-vehicle" value={vehicleId} disabled={Boolean(quote)}
                onChange={(e) => { setVehicleId(e.target.value); setDriverId('') }}>
                <option value="">Choose the car</option>
                {options.vehicles.map((v) => <option key={v.vehicle_id} value={v.vehicle_id}>{v.label}</option>)}
              </NativeSelect>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cr-driver">Driver {driverBorne ? '(name one for the driver borne items)' : '(optional)'}</Label>
              <NativeSelect id="cr-driver" value={driverId} onChange={(e) => setDriverId(e.target.value)} disabled={!vehicle}>
                <option value="">{vehicle && vehicle.drivers.length === 0 ? 'Nobody has driven it in 30 days' : 'No driver named'}</option>
                {(vehicle?.drivers ?? []).map((d) => (
                  <option key={d.driver_id} value={d.driver_id}>{d.driver_name ?? 'Unnamed'}, {d.shifts} shifts in 30 days</option>
                ))}
              </NativeSelect>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cr-reason">What happened</Label>
            <Input id="cr-reason" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Windscreen cracked by a stone on the N1; brake pads squealing; left headlight out" />
          </div>

          <div className="space-y-2 rounded-md border border-dashed border-border p-3">
            <div className="flex flex-wrap items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Quote</span>
              {quote ? (
                <>
                  <span className="text-xs text-foreground">{quote.supplier_name ?? 'Quote'}{quote.invoice_number ? `, no. ${quote.invoice_number}` : ''}</span>
                  {quote.total_zar !== null && <span className="text-xs font-semibold tabular-nums text-foreground">{zarExact(Number(quote.total_zar))}</span>}
                  <Button size="sm" variant="ghost" className="h-6 gap-1 px-2 text-[11px]" onClick={openQuote}>
                    <ExternalLink className="h-3 w-3" /> Open
                  </Button>
                </>
              ) : (
                <span className="text-xs text-muted-foreground">Upload the fitter's quote and the items and amounts are read from it.</span>
              )}
              <input ref={quoteFile} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void readQuote(f) }} />
              {!quote && (
                <Button size="sm" variant="outline" className="ml-auto h-7 gap-1 text-xs" disabled={!vehicleId || uploadQuote.loading}
                  title={!vehicleId ? 'Choose the car first' : undefined} onClick={() => quoteFile.current?.click()}>
                  {uploadQuote.loading ? <><Spinner className="h-3.5 w-3.5" /> Reading the quote…</> : <><Upload className="h-3.5 w-3.5" /> Upload the quote</>}
                </Button>
              )}
            </div>
            {quote?.read_error && <p className="text-[11px] text-rose-700 dark:text-rose-300">{quote.read_error}</p>}
            {(quote?.warnings ?? []).map((w, i) => (
              <p key={i} className="text-[11px] text-amber-700 dark:text-amber-300">Check: {w}</p>
            ))}
            {quote && quote.total_zar !== null && Math.abs(Number(quote.total_zar) - total) >= 0.01 && (
              <p className="text-[11px] text-amber-700 dark:text-amber-300">
                The items below add up to {zarExact(total)} and the quote's total is {zarExact(Number(quote.total_zar))}.
                If the quote charges VAT only on its total, add it to the items, or say why they differ.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Items</Label>
            {items.map((d, i) => {
              const cat = categoryFor(d)
              const borne = cat ? options.borne_by_category[cat] ?? null : null
              return (
                <div key={i} className="flex flex-wrap items-end gap-2 rounded-md border border-border p-2">
                  <div className="space-y-1">
                    <Label htmlFor={`cr-k-${i}`} className="text-[11px]">What</Label>
                    <NativeSelect id={`cr-k-${i}`} className="h-8 w-[200px] text-xs" value={d.kind_key}
                      onChange={(e) => patch(i, { kind_key: e.target.value, category: '' })}>
                      <option value="">Choose</option>
                      {d.kind_key === 'quote' && <option value="quote">As on the quote</option>}
                      {kinds.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
                    </NativeSelect>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`cr-c-${i}`} className="text-[11px]">Category</Label>
                    <NativeSelect id={`cr-c-${i}`} className="h-8 w-[170px] text-xs" value={cat}
                      onChange={(e) => patch(i, { category: e.target.value })}>
                      <option value="">Category</option>
                      {MAINTENANCE_CATEGORIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                    </NativeSelect>
                  </div>
                  <div className="min-w-[160px] flex-1 space-y-1">
                    <Label htmlFor={`cr-d-${i}`} className="text-[11px]">Detail {d.kind_key === 'other' || d.kind_key === 'quote' ? '(needed)' : '(optional)'}</Label>
                    <Input id={`cr-d-${i}`} className="h-8 text-xs" value={d.description}
                      onChange={(e) => patch(i, { description: e.target.value })} placeholder="Front, passenger side" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`cr-a-${i}`} className="text-[11px]">Cash (R)</Label>
                    <Input id={`cr-a-${i}`} className="h-8 w-[110px] text-xs" type="number" inputMode="decimal"
                      value={d.amount} onChange={(e) => patch(i, { amount: e.target.value })} />
                  </div>
                  {borne && <Chip tone={BORNE_TONE[borne]}>{borneLabel(borne)} pays</Chip>}
                  {d.hint && (
                    <p className="basis-full text-[10px] text-muted-foreground">
                      {d.hint}
                    </p>
                  )}
                  {items.length > 1 && (
                    <Button size="sm" variant="ghost" className="h-8 px-2" title="Remove this item"
                      onClick={() => setItems((cur) => cur.filter((_, j) => j !== i))}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              )
            })}
            <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => setItems((cur) => [...cur, blank()])}>
              <Plus className="h-3.5 w-3.5" /> Add an item
            </Button>
          </div>

          {driverBorne && (
            <p className="rounded-md border border-border bg-background p-2 text-[11px] text-muted-foreground">
              Panel beating is driver borne by default. When it is paid, a driver repair case opens for the driver
              named here. Nothing is deducted until a fault finding, a fair hearing and the driver's written
              agreement are on record. If no driver is named, the line waits on the servicing page for one.
            </p>
          )}

          <div className="flex justify-between rounded-md border border-border bg-background p-2 text-sm">
            <span className="font-medium text-foreground">Cash needed</span>
            <span className="font-semibold tabular-nums text-foreground">{zarExact(total)}</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={raise.loading}>Cancel</Button>
          <Button onClick={save} disabled={raise.loading}>
            {raise.loading && <Spinner className="mr-2 h-4 w-4" />} Raise
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
